<template>
  <form @submit.prevent="handleSubmit">
    <div class="form-field">
      <input-text
        elem-id="soComboName"
        data-testid="so-combos-name-input"
        v-model="formData.so_combo_name"
        :label="t('Nome')"
        :placeholder="t('Ex.: New 6')"
      />
      <small v-if="errors.so_combo_name" class="error-message">{{ errors.so_combo_name }}</small>
    </div>

    <div class="form-field">
      <select-list
        elem-id="soComboServiceId"
        data-testid="so-combos-service-select"
        v-model="formData.so_combo_service_id"
        :itens-select="serviceOptions"
        :label="t('Serviço')"
        :placeholder="t('Selecione o serviço')"
        :auto-select-first="false"
      />
      <small v-if="errors.so_combo_service_id" class="error-message">{{ errors.so_combo_service_id }}</small>
    </div>

    <div class="form-field">
      <select-list
        elem-id="soComboPriorityId"
        data-testid="so-combos-priority-select"
        v-model="formData.so_combo_priority_id"
        :itens-select="priorityOptions"
        :label="t('Prioridade')"
        :placeholder="t('Selecione a prioridade')"
        :auto-select-first="false"
      />
      <small v-if="errors.so_combo_priority_id" class="error-message">{{ errors.so_combo_priority_id }}</small>
    </div>

    <div class="flex-container">
      <div class="form-field flex-item-half">
        <input-number
          elem-id="soComboQuantity"
          data-testid="so-combos-quantity-input"
          v-model="formData.so_combo_quantity"
          :label="t('Pares')"
          :min="1"
          :maxFractionDigits="0"
          fluid
        />
        <small v-if="errors.so_combo_quantity" class="error-message">{{ errors.so_combo_quantity }}</small>
      </div>

      <div class="form-field flex-item-half">
        <input-number
          elem-id="soComboValidityDays"
          data-testid="so-combos-validity-input"
          v-model="formData.so_combo_validity_days"
          :label="t('Validade (dias)')"
          :placeholder="t('Vazio: não vence')"
          :min="1"
          :maxFractionDigits="0"
          fluid
        />
        <small v-if="errors.so_combo_validity_days" class="error-message">{{ errors.so_combo_validity_days }}</small>
      </div>
    </div>

    <input-toggle
      elem-id="soComboActive"
      data-testid="so-combos-active-toggle"
      no-explanation="campo do formulário, a regra está na coluna Situação"
      v-model="formData.so_combo_active"
      :label="t('Ativo')"
    />
  </form>
</template>

<script setup lang="ts">
import { reactive, ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSOCombos } from '../../composables/useSOCombos';
import { useSOCatalog } from '../../composables/useSOCatalog';
import { emitValidationFailed } from '../../composables/useFormValidation';
import { eventBus } from '../../eventBus';
import { error as logError } from '../../utils/DebugLogger';

interface KVItem {
  value: number | string;
  label: string;
}

const props = withDefaults(defineProps<{ comboId?: number | null }>(), { comboId: null });

const emit = defineEmits<{
  'submit-success': [];
}>();

const { t } = useI18n();
const { getById, save } = useSOCombos();
const { fetchKV: fetchServicesKV } = useSOCatalog('services');
const { fetchKV: fetchPrioritiesKV } = useSOCatalog('priorities');

const formData = reactive({
  so_combo_name: '',
  so_combo_service_id: undefined as number | string | undefined,
  so_combo_priority_id: undefined as number | string | undefined,
  so_combo_quantity: undefined as number | undefined,
  so_combo_validity_days: undefined as number | null | undefined,
  so_combo_active: true,
});

const errors = reactive({
  so_combo_name: '',
  so_combo_service_id: '',
  so_combo_priority_id: '',
  so_combo_quantity: '',
  so_combo_validity_days: '',
});

const serviceOptions = ref<{ ID: number | string; Name: string }[]>([]);
const priorityOptions = ref<{ ID: number | string; Name: string }[]>([]);

const paraOpcoes = (itens: unknown): { ID: number | string; Name: string }[] =>
  (itens as KVItem[]).map((item) => ({ ID: item.value, Name: item.label }));

const loadOptions = async (): Promise<void> => {
  try {
    const [services, priorities] = await Promise.all([fetchServicesKV(), fetchPrioritiesKV()]);
    serviceOptions.value = paraOpcoes(services);
    priorityOptions.value = paraOpcoes(priorities);
  } catch (error: unknown) {
    logError('SOCombosForm', 'loadOptions', 'Error loading options', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao carregar opcoes') + ': ' + message);
  }
};

const loadForEdit = async (id: number): Promise<void> => {
  try {
    const combo = await getById(id);
    formData.so_combo_name = combo.so_combo_name ?? '';
    formData.so_combo_service_id = combo.so_combo_service_id;
    formData.so_combo_priority_id = combo.so_combo_priority_id;
    formData.so_combo_quantity = combo.so_combo_quantity;
    formData.so_combo_validity_days = combo.so_combo_validity_days ?? null;
    formData.so_combo_active = Number(combo.so_combo_active) === 1;
  } catch (error: unknown) {
    logError('SOCombosForm', 'loadForEdit', 'Error loading combo', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao carregar o combo') + ': ' + message);
  }
};

const validateForm = (): boolean => {
  errors.so_combo_name = formData.so_combo_name.trim() ? '' : t('Campo obrigatório');
  errors.so_combo_service_id = formData.so_combo_service_id ? '' : t('Campo obrigatório');
  errors.so_combo_priority_id = formData.so_combo_priority_id ? '' : t('Campo obrigatório');
  errors.so_combo_quantity = Number.isInteger(formData.so_combo_quantity) && Number(formData.so_combo_quantity) >= 1
    ? ''
    : t('Informe pelo menos 1 par');
  errors.so_combo_validity_days = formData.so_combo_validity_days == null || Number(formData.so_combo_validity_days) >= 1
    ? ''
    : t('Informe pelo menos 1 dia ou deixe vazio');

  const isValid = !Object.values(errors).some(Boolean);
  if (!isValid) {
    emitValidationFailed('SOCombosForm', errors);
  }
  return isValid;
};

const handleSubmit = async (): Promise<void> => {
  if (!validateForm()) return;

  try {
    await save({
      so_combo_name: formData.so_combo_name.trim(),
      so_combo_service_id: Number(formData.so_combo_service_id),
      so_combo_priority_id: Number(formData.so_combo_priority_id),
      so_combo_quantity: Number(formData.so_combo_quantity),
      so_combo_active: formData.so_combo_active ? 1 : 0,
      // Vazio = sem validade: o backend grava NULL quando o campo não vem.
      ...(formData.so_combo_validity_days != null ? { so_combo_validity_days: Number(formData.so_combo_validity_days) } : {}),
      ...(props.comboId ? { so_combo_id: props.comboId } : {}),
    });
    eventBus.emit('globalMessage.success', props.comboId ? t('Combo atualizado') : t('Combo criado'));
    emit('submit-success');
  } catch (error: unknown) {
    logError('SOCombosForm', 'handleSubmit', 'Error submitting form', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao salvar o combo') + ': ' + message);
  }
};

watch(() => props.comboId, (id) => {
  if (id) void loadForEdit(id);
});

onMounted(async () => {
  await loadOptions();
  if (props.comboId) {
    await loadForEdit(props.comboId);
  }
});

defineExpose({ handleSubmit, validateForm });
</script>

<style scoped>
.flex-container {
  display: flex;
  gap: 1rem;
}

.flex-item-half {
  flex: 1;
  min-width: 0;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.error-message {
  color: var(--p-red-500);
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}
</style>
